import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storageModule from 'redux-persist/lib/storage';

import reservationProfilesReducer from './features/access/application/reservationProfilesSlice';
import authReducer from './features/auth/application/authSlice';
import apiResponseStatusReducer from './features/common/application/apiResponseStatusSlice';
import contextReducer from './features/common/application/contextSlice';
import toastReducer from './features/common/application/toastSlice';
import indicatorFiltersReducer from './features/dashboard/application/indicatorFiltersSlice';
import fleetReducer from './features/fleet/application/fleetSlice';
import fleetsReducer from './features/fleet/application/fleetsSlice';
import pendingFleetReducer from './features/fleet/application/pendingFleetSlice';
import pendingVehicleReducer from './features/fleet/application/pendingVehicleSlice';
import vehicleReducer from './features/fleet/application/vehicleSlice';
import vehiclesReducer from './features/fleet/application/vehiclesSlice';
import companyReducer from './features/group/application/companySlice';
import groupReducer from './features/group/application/groupSlice';
import groupsReducer from './features/group/application/groupsSlice';
import memberReducer from './features/group/application/memberSlice';
import membersReducer from './features/group/application/membersSlice';
import pendingGroupReducer from './features/group/application/pendingGroupSlice';
import pendingGuestReducer from './features/group/application/pendingGuestSlice';
import pendingMemberReducer from './features/group/application/pendingMemberSlice';
import quickRegistrationReducer from './features/quickRegistration/application/quickRegistrationSlice';
import holdingReducer from './features/holding/application/holdingSlice';
import holdingsReducer from './features/holding/application/holdingsSlice';
import pendingHoldingReducer from './features/holding/application/pendingHoldingSlice';
import parkingReducer from './features/parking/application/parkingSlice';
import parkingsReducer from './features/parking/application/parkingsSlice';
import subscriptionReducer from './features/subscription/application/subscriptionSlice';
import pendingUserReducer from './features/user/application/pendingUserSlice';
import type { RootState } from './rootState';

import fleetStoreReducer from '@/fleet/data/fleetStore';
import guestReducer from '@/group/application/guestSlice';
import holdingConfigReducer from '@/group/application/holdingConfigSlice';
import invitationsReducer from '@/group/application/invitationsSlice';
import groupStoreReducer from '@/group/data/groupStore';
import parkingStoreReducer from '@/parking/data/parkingStore';
import subscriptionStoreReducer from '@/subscription/data/subscriptionStore';

type PersistStorage = {
    getItem: (key: string) => Promise<string | null> | string | null;
    setItem: (key: string, item: string) => Promise<void> | void;
    removeItem: (key: string) => Promise<void> | void;
};

const storage =
    (storageModule as { default?: PersistStorage }).default ??
    (storageModule);

// Combine reducers
const rootReducer = combineReducers({
    apiResponseStatus: apiResponseStatusReducer,
    auth: authReducer,
    company: companyReducer,
    context: contextReducer,
    fleet: fleetReducer,
    fleets: fleetsReducer,
    group: groupReducer,
    groupStore: groupStoreReducer,
    parkingStore: parkingStoreReducer,
    subscriptionStore: subscriptionStoreReducer,
    fleetStore: fleetStoreReducer,
    groups: groupsReducer,
    guest: guestReducer,
    holding: holdingReducer,
    indicatorFilters: indicatorFiltersReducer,
    holdingConfig: holdingConfigReducer,
    holdings: holdingsReducer,
    invitation: invitationsReducer,
    member: memberReducer,
    members: membersReducer,
    parking: parkingReducer,
    parkings: parkingsReducer,
    reservationProfiles: reservationProfilesReducer,
    pendingFleet: pendingFleetReducer,
    pendingGroup: pendingGroupReducer,
    pendingHolding: pendingHoldingReducer,
    pendingGroupConfigHolding: holdingConfigReducer,
    pendingGuest: pendingGuestReducer,
    pendingMember: pendingMemberReducer,
    pendingUserProfile: pendingUserReducer,
    pendingVehicle: pendingVehicleReducer,
    quickRegistration: quickRegistrationReducer,
    subscription: subscriptionReducer,
    toast: toastReducer,
    vehicle: vehicleReducer,
    vehicles: vehiclesReducer,


    // Add other reducers here
});

// Configure persistor
const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth', 'indicatorFilters'], // Reducers to persist
};

// Create persisted reducer
const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

// Create store
const store = configureStore({
    // Redux Toolkit wires up the Redux DevTools extension itself (no `composeWithDevTools` needed).
    // Enabled everywhere except a production build — flip to `true` to inspect state in prod too.
    devTools: !import.meta.env.PROD,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'], // Ignore specific redux-persist actions
                ignoredPaths: ['apiResponseStatus'], // Ignore specific state paths
            },
        }),
    reducer: persistedReducer,
});

// Create persistor
const persistor = persistStore(store);

export { store, persistor };

